import React from 'react';
import { pdfFontFamily } from '../../../../utils/pdf';
import type { IdCardAudience, IdCardDesignInput } from '../../../../types/idCards';
import { fieldDef } from '../../catalog';
import { initialsOf, type CardPerson } from '../../cardPeople';
import { cardPalette, type CardPalette } from '../cardColors';
import { V, T, Img, type CardStyle } from '../primitives';

/** The school as a card shows it. The logo is a data URL, ready to draw. */
export interface CardSchool {
  name: string;
  logo: string | null;
  /** A larger logo for the back's watermark; the logo itself when absent. */
  watermark?: string | null;
  address?: string;
  phone?: string;
}

export interface CardFaceProps {
  design: IdCardDesignInput & { audience: IdCardAudience };
  person: CardPerson;
  /** The person's photo as a data URL, or null for initials. */
  photo: string | null;
  school: CardSchool;
  /** "30 Jun 2027", or null to leave the validity line off. */
  validUntil: string | null;
}

export interface Detail { label: string; value: string; mono?: boolean; wide?: boolean }

/** What every face derives from its props: the colours and the details to print, in order. */
export const faceData = ({ design, person }: CardFaceProps): { palette: CardPalette; details: Detail[] } => ({
  palette: cardPalette(design.colors.primary, design.colors.accent),
  details: design.fields.flatMap(key => {
    const def = fieldDef(design.audience, key);
    return def ? [{ label: def.label, value: person.values[key] || '—', mono: def.mono, wide: def.wide }] : [];
  }),
});

// ── Type scale (points) — tuned for an 85.6 × 54 mm card at arm's length ────

export const type = {
  label: { fontSize: 4.3, fontWeight: 600, letterSpacing: 0.55, textTransform: 'uppercase' } as CardStyle,
  value: { fontSize: 6.3, fontWeight: 600, lineHeight: 1.25 } as CardStyle,
  caps: { fontSize: 4.6, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase' } as CardStyle,
  mono: { fontFamily: pdfFontFamily.mono, fontWeight: 700, letterSpacing: 0.2 } as CardStyle,
};

/** The holder's name — a step smaller when it is long, so it holds two lines at most. */
export const nameStyle = (name: string, size: number): CardStyle => ({
  fontSize: name.length > 24 ? size * 0.86 : size,
  fontWeight: 700,
  letterSpacing: -0.15,
  lineHeight: 1.15,
});

/** Every face's base: the card fonts, and nothing spills past the card edge. */
export const faceBase = (width: number, height: number, background: string): CardStyle => ({
  width, height, backgroundColor: background, overflow: 'hidden', fontFamily: pdfFontFamily.sans,
});

export const DetailItem: React.FC<{ d: Detail; palette: CardPalette; align?: 'left' | 'center'; style?: CardStyle }> = ({ d, palette, align = 'left', style }) => (
  <V style={[{ alignItems: align === 'center' ? 'center' : 'flex-start', overflow: 'hidden' }, style]}>
    <T style={[type.label, { color: palette.inkFaint }]}>{d.label}</T>
    <T style={[type.value, { color: palette.ink, marginTop: 1, textAlign: align }, d.mono ? type.mono : {}]} lines={1}>{d.value}</T>
  </V>
);

/** Details two to a row. */
export const DetailGrid: React.FC<{ details: Detail[]; palette: CardPalette; align?: 'left' | 'center'; gap?: number }> = ({ details, palette, align, gap = 4.5 }) => (
  <V style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
    {details.map((d, i) => {
      // A wide detail takes the row; so does a centred grid's odd last one.
      const alone = d.wide || (align === 'center' && i === details.length - 1 && details.length % 2 === 1);
      return <DetailItem key={i} d={d} palette={palette} align={align} style={{ width: alone ? '100%' : '50%', paddingRight: alone ? 0 : 4, marginBottom: gap }} />;
    })}
  </V>
);

/** The school logo, on a white tile when it sits on colour. */
export const Logo: React.FC<{ school: CardSchool; size: number; palette: CardPalette; tile?: boolean; round?: boolean }> = ({ school, size, palette, tile, round }) => {
  const inner = tile ? size * 0.78 : size;
  const mark = school.logo
    ? <Img src={school.logo} style={{ width: inner, height: inner, objectFit: 'contain' }} />
    : (
      <V style={{ width: inner, height: inner, borderRadius: round ? inner / 2 : inner * 0.22, backgroundColor: palette.primary, alignItems: 'center', justifyContent: 'center' }}>
        <T style={{ fontSize: inner * 0.48, fontWeight: 700, color: palette.onPrimary }}>{initialsOf(school.name).slice(0, 1)}</T>
      </V>
    );
  if (!tile) return mark;
  return (
    <V style={{ width: size, height: size, borderRadius: round ? size / 2 : size * 0.24, backgroundColor: palette.paper, alignItems: 'center', justifyContent: 'center' }}>
      {mark}
    </V>
  );
};

/** The holder's photo, or their initials on a soft tint when there is none.
 *  `ring` draws a border of that width, and the photo sits inside it. */
export const Photo: React.FC<{ photo: string | null; name: string; width: number; height: number; radius: number; palette: CardPalette; ring?: { width: number; color: string }; style?: CardStyle }> = ({ photo, name, width, height, radius, palette, ring, style }) => {
  const inset = ring?.width ?? 0;
  return (
    <V style={[{
      width, height, borderRadius: radius, overflow: 'hidden', backgroundColor: palette.wash, alignItems: 'center', justifyContent: 'center',
    }, ring ? { borderWidth: inset, borderColor: ring.color, borderStyle: 'solid' } : {}, style ?? {}]}>
      {photo
        ? <Img src={photo} style={{ width: width - 2 * inset, height: height - 2 * inset, borderRadius: Math.max(0, radius - inset), objectFit: 'cover' }} />
        : <T style={{ fontSize: Math.min(width, height) * 0.34, fontWeight: 700, color: palette.primaryText, letterSpacing: 0.5 }}>{initialsOf(name)}</T>}
    </V>
  );
};

const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' } as const;

/** "Valid until 30 Jun 2027" — two texts side by side, not one nested in
 *  the other: react-pdf mis-measures nested text of mixed sizes and drops
 *  it from a fixed-height row. */
export const Validity: React.FC<{ validUntil: string | null; color: string; labelColor: string; align?: 'left' | 'center' | 'right' }> = ({ validUntil, color, labelColor, align = 'left' }) => (
  validUntil ? (
    <V style={{ flexDirection: 'row', alignItems: 'center', justifyContent: JUSTIFY[align] }}>
      <T style={[type.label, { color: labelColor, marginRight: 3 }]}>Valid until</T>
      <T style={{ fontSize: 5, fontWeight: 700, color }}>{validUntil}</T>
    </V>
  ) : null
);
